/**
 * Retour sonore des scans.
 *
 * Les sons sont synthétisés à la volée (Web Audio) plutôt que chargés depuis
 * des fichiers : rien à télécharger, aucune latence au premier scan, et le
 * retour reste instantané même sans réseau — ce qui compte quand le son sert
 * à confirmer un geste répété des centaines de fois par soirée.
 *
 * Les deux signaux sont volontairement opposés sur tous les axes perceptifs :
 *   valide   → deux notes claires qui MONTENT (880 Hz → 1319 Hz), timbre doux
 *   invalide → deux impulsions graves qui DESCENDENT (200 Hz → 120 Hz), timbre dur
 * Aigu/grave, montant/descendant, doux/rêche : reconnaissable dans le bruit
 * d'une foule sans avoir à écouter attentivement.
 */

const SOUND_PREF_KEY = 'soundEnabled'

let audioContext = null

const getContext = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext
  if (!AudioCtx) return null
  if (!audioContext) {
    audioContext = new AudioCtx()
  }
  return audioContext
}

/** Le son est actif par défaut ; l'employé peut le couper dans les réglages. */
export const isSoundEnabled = () => {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) !== 'false'
  } catch {
    return true
  }
}

export const setSoundEnabled = (enabled) => {
  try {
    localStorage.setItem(SOUND_PREF_KEY, String(enabled))
  } catch {
    /* stockage indisponible : on garde le comportement par défaut */
  }
}

/**
 * Les navigateurs mobiles démarrent tout AudioContext à l'état « suspended »
 * et n'autorisent sa reprise que pendant une interaction utilisateur. On la
 * déclenche donc au premier appui, quel qu'il soit.
 */
export const primeAudio = () => {
  const ctx = getContext()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }
}

/**
 * Une note : oscillateur + enveloppe attaque / maintien / extinction.
 *
 * Le maintien est indispensable : avec une simple décroissance exponentielle,
 * le niveau s'effondre en quelques dizaines de millisecondes et la note passe
 * inaperçue dans le bruit d'une file d'attente. `release` fixe la part de la
 * durée consacrée à l'extinction — courte pour un signal net et sec, longue
 * pour un carillon qui s'éteint doucement.
 *
 * Les rampes exponentielles évitent le « clic » d'un démarrage/arrêt brutal.
 * Elles ne peuvent pas atteindre zéro, d'où le plancher à 0.0001.
 */
const playTone = (
  ctx,
  { startAt, duration, from, to, type = 'sine', peak = 0.2, lowpass, release = 0.5, attack = 0.012 }
) => {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillator.type = type
  oscillator.frequency.setValueAtTime(from, startAt)
  if (to && to !== from) {
    oscillator.frequency.exponentialRampToValueAtTime(to, startAt + duration)
  }

  const releaseTime = Math.max(0.03, duration * release)
  const holdUntil = startAt + Math.max(attack, duration - releaseTime)

  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(peak, startAt + attack)
  gain.gain.setValueAtTime(peak, holdUntil)
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)

  let node = oscillator
  if (lowpass) {
    // Adoucit les harmoniques de l'onde carrée, agressives sur haut-parleur
    // de téléphone une fois le volume poussé.
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(lowpass, startAt)
    node.connect(filter)
    node = filter
  }

  node.connect(gain)
  gain.connect(ctx.destination)

  oscillator.start(startAt)
  oscillator.stop(startAt + duration + 0.02)
}

const play = (build) => {
  if (!isSoundEnabled()) return
  const ctx = getContext()
  if (!ctx) return

  // Nouvelle tentative de reprise : si l'appareil n'a encore reçu aucune
  // interaction, le contexte est muet et l'appel reste sans effet.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }

  try {
    build(ctx, ctx.currentTime)
  } catch {
    /* le son ne doit jamais empêcher l'affichage du verdict */
  }
}

/**
 * Billet accepté : deux notes claires et montantes, extinction en carillon.
 * Bref (~280 ms) pour ne pas ralentir une file qui avance vite.
 */
export const playValidSound = () =>
  play((ctx, now) => {
    playTone(ctx, {
      startAt: now,
      duration: 0.12,
      from: 880,
      type: 'sine',
      peak: 0.22,
      release: 0.45,
    })
    playTone(ctx, {
      startAt: now + 0.1,
      duration: 0.22,
      from: 1318.5,
      type: 'sine',
      peak: 0.22,
      release: 0.6,
    })
  })

/**
 * Billet refusé : deux impulsions graves et descendantes, tenues et coupées
 * net. Volontairement plus long que le son d'acceptation — un refus demande
 * une action de l'employé, il ne doit pas pouvoir passer inaperçu.
 */
export const playInvalidSound = () =>
  play((ctx, now) => {
    playTone(ctx, {
      startAt: now,
      duration: 0.16,
      from: 200,
      to: 175,
      type: 'square',
      peak: 0.13,
      lowpass: 900,
      release: 0.22,
      attack: 0.008,
    })
    playTone(ctx, {
      startAt: now + 0.21,
      duration: 0.3,
      from: 165,
      to: 120,
      type: 'square',
      peak: 0.13,
      lowpass: 900,
      release: 0.22,
      attack: 0.008,
    })
  })

/** Raccourci : joue le signal correspondant au résultat d'une validation. */
export const playScanResult = (isValid) =>
  isValid ? playValidSound() : playInvalidSound()
